/* global axios */
import { useAlert } from 'dashboard/composables';
import { notifyTaskCompleted } from 'dashboard/helper/onboardingSync';
import { isOrganizationAccount } from 'dashboard/helper/organizationAccessHelper';

const knowledgeProxy = (accountId, path) =>
  `/api/v1/accounts/${accountId}/proxy/knowledge${path}`;

/**
 * ─────────────────────────────────────────────
 * Onboarding versioning
 * ─────────────────────────────────────────────
 */
// Version 2 tasks are organization-flavored; only shown to accounts using
// organization instead of the legacy branch feature (see
// helper/organizationAccessHelper.js for what determines that).
const ONBOARDING_VERSION = 2;

const TASKS_BY_VERSION = {
  1: [
    {
      id: 'create_knowledge_base',
      title: 'Create your first knowledge base',
      description: 'Create a knowledge base to store your FAQs.',
      routeName: 'knowledge',
      url: '/knowledge',
      external: false,
    },
    {
      id: 'create_inbox',
      title: 'Create a channel',
      description: 'Create WhatsApp, Telegram, or other channel.',
      routeName: 'settings_inbox_list',
      url: '/settings/inboxes',
      external: false,
    },
    {
      id: 'configure_bot',
      title: 'Configure your first bot',
      description:
        'Add knowledge base to your bot, and setup the instructions in your inbox settings.',
      routeName: 'settings_inbox_list',
      url: '/settings/inboxes',
      external: false,
    },
  ],
  2: [
    {
      id: 'create_staff',
      title: 'Create your first staff',
      description:
        'Create a staff that can be assigned with a service in your branch.',
      routeName: 'organization.staffs',
      url: '/organization/staffs',
      external: false,
    },
    {
      id: 'create_service',
      title: 'Create your first service',
      description:
        'Create a service that can be assigned to a staff in your branch.',
      routeName: 'organization.services',
      url: '/organization/services',
      external: false,
    },
    {
      id: 'create_branch',
      title: 'Create your first branch',
      description:
        'Create a branch where you can manage schedules, services and staff.',
      routeName: 'organization.branches',
      url: '/organization/branches',
      external: false,
    },
    {
      id: 'setup_branch_operating_hours',
      title: 'Setup branch operating hours',
      description: 'Configure the operating hours for your branch.',
      routeName: 'organization.branches',
      url: '/organization/branches',
      external: false,
    },
    {
      id: 'link_staff_to_branch',
      title: 'Link staff to branch',
      description: 'Assign staff members to your branch.',
      routeName: 'organization.branches',
      url: '/organization/branches',
      external: false,
    },
    {
      id: 'link_service_to_branch',
      title: 'Link service to branch',
      description: 'Assign services to your branch.',
      routeName: 'organization.branches',
      url: '/organization/branches',
      external: false,
    },
    {
      id: 'link_staff_to_service',
      title: 'Link branch staff to branch service',
      description: 'Assign staff members to specific services in your branch.',
      routeName: 'organization.branches',
      url: '/organization/branches',
      external: false,
    },
    {
      id: 'setup_staff_schedule',
      title: 'Setup staff schedule',
      description: 'Create a schedule for managing staff availability.',
      routeName: 'organization.branches',
      url: '/organization/branches',
      external: false,
    },
    {
      id: 'link_staff_to_schedule',
      title: 'Link branch staff to a staff schedule',
      description: 'Assign staff members to a schedule.',
      routeName: 'organization.branches',
      url: '/organization/branches',
      external: false,
    },
  ],
};

/**
 * Flatten tasks up to a version
 * Restricts version 2 tasks to organization-enabled accounts
 */
function getTasksUpToVersion(version, isOrganizationEnabled = false) {
  const maxVersion = isOrganizationEnabled ? version : Math.min(version, 1);

  return Object.keys(TASKS_BY_VERSION)
    .map(Number)
    .filter(v => v <= maxVersion)
    .flatMap(v => TASKS_BY_VERSION[v]);
}

/**
 * Migrate backend tasks → frontend task list
 */
function migrateTasks({ backendTasks = [], isOrganizationEnabled = false }) {
  const allTasks = getTasksUpToVersion(
    ONBOARDING_VERSION,
    isOrganizationEnabled
  );

  const backendStatusMap = backendTasks.reduce((acc, t) => {
    acc[t.key] = t.status;
    return acc;
  }, {});

  return allTasks.map(task => ({
    ...task,
    completed: backendStatusMap[task.id] === 'completed',
  }));
}

export const state = {
  onboarding: null,
  localTasks: [],
  isLoading: false,
  isError: false,
  initializing: false,
  lastCheckedAt: null,
  milestones: null,
  milestonesFetchedAccountId: null,
};

export const getters = {
  onboarding: state => state.onboarding,
  localTasks: state => state.localTasks,
  isLoading: state => state.isLoading,
  isError: state => state.isError,
  initializing: state => state.initializing,
  lastCheckedAt: state => state.lastCheckedAt,
  remainingLocalCount: state =>
    state.localTasks.filter(t => !t.completed).length,
  incompleteLocalTasks: state => state.localTasks.filter(t => !t.completed),
  getMilestones: state => state.milestones,
  getMilestonesFetchedAccountId: state => state.milestonesFetchedAccountId,
};

export const mutations = {
  SET_ONBOARDING(state, onboarding) {
    state.onboarding = onboarding;
  },

  SET_LOCAL_TASKS(state, tasks) {
    state.localTasks = tasks;
  },

  MARK_LOCAL_TASK_COMPLETED(state, taskId) {
    const idx = state.localTasks.findIndex(t => t.id === taskId);
    if (idx !== -1) {
      state.localTasks.splice(idx, 1, {
        ...state.localTasks[idx],
        completed: true,
      });
    }
  },

  MARK_ALL_LOCAL_COMPLETED(state) {
    state.localTasks = state.localTasks.map(t => ({ ...t, completed: true }));
  },

  // Newly added: mark all local tasks as *not* completed
  MARK_ALL_LOCAL_UNCOMPLETED(state) {
    state.localTasks = state.localTasks.map(t => ({ ...t, completed: false }));
  },

  SET_LOADING(state, isLoading) {
    state.isLoading = isLoading;
  },

  SET_ERROR(state, isError) {
    state.isError = isError;
  },

  SET_INITIALIZING(state, bool) {
    state.initializing = bool;
  },

  SET_LAST_CHECKED(state, timestamp) {
    state.lastCheckedAt = timestamp;
  },

  SET_MILESTONES(state, milestones) {
    state.milestones = milestones;
  },

  SET_MILESTONES_FETCHED_ACCOUNT_ID(state, accountId) {
    state.milestonesFetchedAccountId = accountId;
  },
};

export const actions = {
  // Fetch onboarding data from backend
  async fetchOnboarding({ commit, rootGetters }) {
    commit('SET_LOADING', true);
    commit('SET_ERROR', false);

    const accountId = rootGetters.getCurrentAccountId;

    try {
      const { data: res } = await axios.get(
        knowledgeProxy(accountId, `/account/${accountId}/onboarding`)
      );

      const onboardingData = res.onboarding || {};
      commit('SET_ONBOARDING', onboardingData);
      commit('SET_LAST_CHECKED', new Date().toISOString());
      return onboardingData;
    } catch (error) {
      commit('SET_ERROR', true);
      throw error.response?.data || error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  // Update onboarding data on backend
  async updateOnboarding({ commit, rootGetters }, onboardingPayload) {
    commit('SET_LOADING', true);
    commit('SET_ERROR', false);

    const accountId = rootGetters.getCurrentAccountId;

    try {
      // Always wrap payload in { onboarding: {...} } as per backend schema
      const { data: res } = await axios.put(
        knowledgeProxy(accountId, `/account/${accountId}/onboarding`),
        { onboarding: onboardingPayload }
      );

      const onboardingData = res.onboarding || {};
      commit('SET_ONBOARDING', onboardingData);
      commit('SET_LAST_CHECKED', new Date().toISOString());
      return onboardingData;
    } catch (error) {
      commit('SET_ERROR', true);
      throw error.response?.data || error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  // Initialize onboarding (migrate if needed, populate defaults)
  async initializeOnboarding({ commit, dispatch, rootGetters }) {
    commit('SET_INITIALIZING', true);

    try {
      const onboarding = await dispatch('fetchOnboarding');
      const accountId = rootGetters.getCurrentAccountId?.toString();

      const backendVersion = Number(onboarding?.version || 0);
      const backendTasks = onboarding?.tasks || [];

      // Determine effective version based on account access
      const isOrgEnabled = isOrganizationAccount(
        { getters: rootGetters },
        accountId
      );
      const effectiveVersion = isOrgEnabled ? ONBOARDING_VERSION : 1;

      // Version outdated → migrate tasks
      if (backendVersion < effectiveVersion) {
        const migratedTasks = migrateTasks({
          backendTasks,
          isOrganizationEnabled: isOrgEnabled,
        });
        commit('SET_LOCAL_TASKS', migratedTasks);

        await dispatch('updateOnboarding', {
          version: effectiveVersion,
          tasks: migratedTasks.map(t => ({
            key: t.id,
            status: t.completed ? 'completed' : 'pending',
          })),
        });

        return migratedTasks;
      }

      // Backend tasks exist → map normally (filter by account access)
      if (Array.isArray(backendTasks) && backendTasks.length > 0) {
        const mapped = backendTasks
          .map(t => {
            const def = getTasksUpToVersion(
              effectiveVersion,
              isOrgEnabled
            ).find(d => d.id === t.key);

            return {
              ...(def || {}),
              id: t.key,
              completed: t.status === 'completed',
            };
          })
          .filter(t => t.title); // Filter out tasks without definitions (version restricted)

        commit('SET_LOCAL_TASKS', mapped);
        return mapped;
      }

      // Backend has no tasks → populate default
      const defaultTasks = getTasksUpToVersion(
        effectiveVersion,
        isOrgEnabled
      ).map(t => ({
        ...t,
        completed: false,
      }));
      commit('SET_LOCAL_TASKS', defaultTasks);

      await dispatch('updateOnboarding', {
        version: effectiveVersion,
        tasks: defaultTasks.map(t => ({
          key: t.id,
          status: 'pending',
        })),
      });

      return defaultTasks;
    } finally {
      commit('SET_INITIALIZING', false);
    }
  },

  // Open task in new window
  openTask(_, task) {
    if (!task?.url) return;
    window.open(task.url, '_blank', 'noopener,noreferrer');
  },

  // Complete a single local task
  async completeLocalTask({ commit, state, dispatch, rootGetters }, taskId) {
    const task = state.localTasks.find(t => t.id === taskId);

    if (!task || task.completed) {
      return true;
    }

    commit('MARK_LOCAL_TASK_COMPLETED', taskId);
    notifyTaskCompleted(taskId);

    const accountId = rootGetters.getCurrentAccountId?.toString();
    const hasOrganizationAccess = isOrganizationAccount(
      { getters: rootGetters },
      accountId
    );
    const effectiveVersion = hasOrganizationAccess ? ONBOARDING_VERSION : 1;

    try {
      await dispatch('updateOnboarding', {
        version: effectiveVersion,
        tasks: state.localTasks.map(t => ({
          key: t.id,
          status: t.completed ? 'completed' : 'pending',
        })),
      });

      await dispatch('fetchOnboarding');

      useAlert(`Task completed: ${task.title}`);
      return true;
    } catch (err) {
      // Rollback on failure
      commit(
        'SET_LOCAL_TASKS',
        state.localTasks.map(t =>
          t.id === taskId ? { ...t, completed: false } : t
        )
      );
      throw err;
    }
  },

  // Skip all tasks (mark as completed)
  async skipAllLocalTasks({ commit, dispatch, state, rootGetters }) {
    commit('MARK_ALL_LOCAL_COMPLETED');

    const accountId = rootGetters.getCurrentAccountId?.toString();
    const hasOrganizationAccess = isOrganizationAccount(
      { getters: rootGetters },
      accountId
    );
    const effectiveVersion = hasOrganizationAccess ? ONBOARDING_VERSION : 1;

    try {
      await dispatch('updateOnboarding', {
        version: effectiveVersion,
        tasks: state.localTasks.map(t => ({
          key: t.id,
          status: 'completed',
        })),
      });

      await dispatch('fetchOnboarding');
      return true;
    } catch (err) {
      // Rollback local state if backend fails
      commit(
        'SET_LOCAL_TASKS',
        state.localTasks.map(t => ({ ...t, completed: false }))
      );
      throw err;
    }
  },

  // Newly added: Undo all tasks (mark all as not completed)
  async undoAllLocalTasks({ commit, dispatch, state, rootGetters }) {
    commit('MARK_ALL_LOCAL_UNCOMPLETED');

    const accountId = rootGetters.getCurrentAccountId?.toString();
    const hasOrganizationAccess = isOrganizationAccount(
      { getters: rootGetters },
      accountId
    );
    const effectiveVersion = hasOrganizationAccess ? ONBOARDING_VERSION : 1;

    try {
      await dispatch('updateOnboarding', {
        version: effectiveVersion,
        tasks: state.localTasks.map(t => ({
          key: t.id,
          status: 'pending',
        })),
      });

      await dispatch('fetchOnboarding');
      return true;
    } catch (err) {
      // Rollback local state if backend fails (mirror skipAllLocalTasks behavior)
      commit(
        'SET_LOCAL_TASKS',
        state.localTasks.map(t => ({ ...t, completed: true }))
      );
      throw err;
    }
  },

  // Separate, additive endpoint from the task-checklist onboarding above —
  // drives the DRP-1 wizard funnel metrics. Idempotent on the backend, so
  // safe to call on every relevant mount/success without tracking whether
  // it was already sent. Also doubles as the milestones-cache refresh (see
  // getMilestones/fetchOnboardingMilestones below) since its response
  // always includes the full current milestones map.
  async markMilestone({ commit, rootGetters }, key) {
    const accountId = rootGetters.getCurrentAccountId;

    try {
      const { data } = await axios.post(
        knowledgeProxy(accountId, `/account/${accountId}/onboarding/milestone`),
        { key }
      );
      commit('SET_MILESTONES', data?.milestones || {});
      commit('SET_MILESTONES_FETCHED_ACCOUNT_ID', accountId);
      return data;
    } catch (error) {
      // Non-critical — funnel tracking shouldn't block the user's flow.
      console.error('Failed to mark onboarding milestone', key, error);
      return null;
    }
  },

  // Explicit-accountId read path for the global onboarding route guard
  // (helper/onboardingRouteGuard.js). That guard runs in router.beforeEach,
  // which fires before vuex-router-sync updates the store's route state —
  // so rootGetters.getCurrentAccountId (used by markMilestone above) would
  // still resolve to the *previous* route's account there. Takes the
  // destination accountId explicitly instead, same reasoning as
  // stripe.js's fetchSubscription/checkSubscriptionStatus.
  async fetchOnboardingMilestones({ commit, state }, accountId) {
    // Clear any other account's cached milestones up front, before making
    // the request — so if this fetch fails, getMilestones/getMilestonesFetchedAccountId
    // come back null/unset (the guard's existing "inconclusive, fail open"
    // path) instead of silently leaving the previous account's milestones
    // in place for the route guard to misread as this account's result.
    if (state.milestonesFetchedAccountId !== accountId) {
      commit('SET_MILESTONES', null);
      commit('SET_MILESTONES_FETCHED_ACCOUNT_ID', null);
    }

    try {
      const { data } = await axios.post(
        knowledgeProxy(accountId, `/account/${accountId}/onboarding/milestone`),
        { key: 'started' }
      );
      commit('SET_MILESTONES', data?.milestones || {});
      commit('SET_MILESTONES_FETCHED_ACCOUNT_ID', accountId);
      return data;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to fetch onboarding milestones', error);
      return null;
    }
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
